<template>
	<v-card>
		<v-card-title>
			<v-layout row wrap>
				<v-flex lg4 md6 sm6 xs12 class="pr-3">
					<v-text-field v-model="search" append-icon="search" label="Search" autofocus=""></v-text-field>
				</v-flex>
			</v-layout>
			<v-layout row wrap>
				<v-spacer></v-spacer>
				<v-btn color="primary" @click="onAddLocation">Add new location</v-btn>
			</v-layout>
		</v-card-title>
		<v-data-table disable-initial-sort :headers="headers" :items="locations" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" :search="search">
			<template slot="items" slot-scope="props">
				<tr>
					<td class="text-xs-left">{{ props.item.name }}</td>
					<td class="text-xs-left">{{ props.item.description }}</td>
					<td class="text-xs-left">{{ formatAddress(props.item) }}</td>
					<td class="justify-center px-0">
						<v-menu offset-y>
							<template v-slot:activator="{ on }">
								<v-btn small icon flat v-on="on">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<v-list>
								<v-list-tile v-for="(action, index) in getAvailableActions(props.item)" :key="index" @click="action.callback">
									<v-list-tile-action
										><v-icon>{{ action.icon }}</v-icon></v-list-tile-action
									>
									<v-list-tile-title>{{ action.title }}</v-list-tile-title>
								</v-list-tile>
							</v-list>
						</v-menu>
					</td>
				</tr>
			</template>
		</v-data-table>

		<v-dialog v-model="showLocationEditor" max-width="500px" persistent>
			<AssetLocationEditor v-if="showLocationEditor" ref="locationEditor" @onSave="onSaveLocation" :is-saving="isUpdatingConfig" :location="locationForEditor" @closeEditor="showLocationEditor = false" />
		</v-dialog>

		<confirmation-dialog
			v-if="showConfirmDeleteDialog"
			:dialog="showConfirmDeleteDialog"
			:title="'Delete Location: ' + locationBeingDeleted.name"
			text="Are you sure you want to delete this location?"
			:isConfirming="isDeleting"
			@onConfirm="onDeleteConfirm"
			@onCancel="showConfirmDeleteDialog = false"
		>
			<div v-if="assetsForLocationDeleting.length > 0">
				<v-alert :value="true" type="warning" outline class="mt-2 text-xs-left">
					You currently have <strong>{{ assetsForLocationDeleting.length }}</strong> asset{{ assetsForLocationDeleting.length > 1 ? 's' : '' }} assigned to this location.
					<span v-if="locations.length > 1">You can either re-assign to another location or remove.</span>
					<span v-else>This will remove the location from all associated assets.</span>
				</v-alert>

				<div class="mt-4" v-if="locations.length > 1">
					<p>What would you like to do?</p>
					<v-flex class="mt-4">
						<v-btn-toggle v-model="deletingAction">
							<v-btn flat value="assign" mandatory>
								Re-assign
							</v-btn>
							<v-btn flat value="remove">
								Remove Location
							</v-btn>
						</v-btn-toggle>
					</v-flex>
					<v-select v-if="deletingAction === 'assign'" v-model="reassignLocationId" class="mt-4" :items="locationsForReassign" item-text="name" item-value="id" label="Select a location" />
				</div>
			</div>
		</confirmation-dialog>
	</v-card>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import AssetLocationEditor from '@components/assets/AssetLocationEditor.vue';

	export default {
		components: { AssetLocationEditor },
		data() {
			return {
				headers: [
					{ text: 'Name', value: 'name' },
					{ text: 'Description', value: 'description' },
					{ text: 'Address', value: 'addressLine1' },
					{ text: '', value: 'actions', sortable: false }
				],
				search: null,
				locationForEditor: null,
				showLocationEditor: false,

				isDeleting: false,
				showConfirmDeleteDialog: false,
				deletingId: null,
				deletingAction: 'remove',
				reassignLocationId: null,
				pagination: {
					sortBy: 'name',
					rowsPerPage: 12
				},
				rowsPerPageItems: [12, 30, 60, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
			};
		},
		computed: {
			...mapGetters({
				locations: 'assetConfiguration/locations',
				isUpdatingConfig: 'assetConfiguration/isSaving',
				getAssetsByLocation: 'assets/getAssetsByLocation'
			}),
			assetsForLocationDeleting() {
				return this.getAssetsByLocation(this.deletingId);
			},
			locationsForReassign() {
				return this.locations.filter(x => x.id !== this.deletingId);
			},
			locationBeingDeleted() {
				return this.locations.find(x => x.id === this.deletingId);
			}
		},
		methods: {
			...mapActions({
				upsertLocation: 'assetConfiguration/upsertAssetLocation',
				deleteLocation: 'assetConfiguration/deleteLocation',
				bulkUpdateAssets: 'assets/updateBulk'
			}),
			onAddLocation() {
				this.showLocationEditor = true;
				this.locationForEditor = null;
			},
			onEditClick(item) {
				this.showLocationEditor = true;
				this.locationForEditor = item;
			},
			onDeleteClick(id) {
				this.showConfirmDeleteDialog = true;
				this.deletingId = id;
				this.reassignLocationId = this.locationsForReassign.length > 0 ? this.locationsForReassign[0].id : null;
			},
			async onSaveLocation(location) {
				const result = await this.upsertLocation(location);

				if (result.isSuccess) {
					this.showLocationEditor = false;
				}
			},
			async onDeleteConfirm() {
				if (this.assetsForLocationDeleting.length > 0) {
					this.isDeleting = true;
					const bulkUpdateDto = this.assetsForLocationDeleting.map(x => {
						return {
							id: x.id,
							categoryId: x.categoryId,
							currentLocationId: this.deletingAction === 'assign' ? this.reassignLocationId : null,
							status: x.status
						};
					});
					const updateRes = await this.bulkUpdateAssets(bulkUpdateDto);

					if (!updateRes.isSuccess) {
						this.isDeleting = false;
						return;
					}
				}

				const result = await this.deleteLocation(this.deletingId);

				if (result.isSuccess) {
					this.deletingId = null;
					this.showConfirmDeleteDialog = false;
				}
				this.isDeleting = false;
			},
			getAvailableActions(item) {
				return [
					{
						title: 'Edit',
						icon: 'edit',
						callback: () => this.onEditClick(item)
					},
					{
						title: 'Delete',
						icon: 'delete',
						callback: () => this.onDeleteClick(item.id)
					}
				];
			},
			formatAddress(asset) {
				const { addressLine1, city, postcode } = asset;

				if (!addressLine1 && !city && !postcode) {
					return '';
				}

				const addressParts = [addressLine1, city, postcode].filter(Boolean);
				return addressParts.join(', ');
			}
		}
	};
</script>
